import { motion } from 'framer-motion';
import { LightBulbIcon, BookOpenIcon, UserGroupIcon, AcademicCapIcon } from '@heroicons/react/24/outline';
import { SuggestionsListProps } from '../../../types';

const SuggestionsList: React.FC<SuggestionsListProps> = ({ suggestions }) => {
  const getIcon = (category: string) => {
    const iconMap: Record<string, React.ReactNode> = {
      'Structured Learning': <BookOpenIcon className="h-6 w-6" />,
      'Collaborative Learning': <UserGroupIcon className="h-6 w-6" />,
      'Creative Expression': <LightBulbIcon className="h-6 w-6" />,
      'Differentiated Instruction': <AcademicCapIcon className="h-6 w-6" />
    };
    return iconMap[category] || <LightBulbIcon className="h-6 w-6" />;
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h3 className="text-2xl font-bold text-gray-900 mb-4">Pedagogical Recommendations</h3>
        <p className="text-gray-600 max-w-3xl mx-auto">
          Based on your classroom insights, student feedback, and evidence-based pedagogical research, 
          here are specific, actionable suggestions to enhance your teaching effectiveness.
        </p>
      </div>

      {suggestions.map((suggestion, index) => (
        <motion.div
          key={suggestion.category}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: index * 0.1 }}
          className="bg-white rounded-xl shadow-lg p-6 border-l-4 border-emerald-500"
        >
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center text-emerald-600">
                {getIcon(suggestion.category)}
              </div>
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-xl font-semibold text-gray-900">{suggestion.category}</h4>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                  suggestion.priority === 'High' 
                    ? 'bg-red-100 text-red-800' 
                    : suggestion.priority === 'Medium'
                    ? 'bg-yellow-100 text-yellow-800'
                    : 'bg-green-100 text-green-800'
                }`}>
                  {suggestion.priority} Priority
                </span>
              </div>
              
              <div className="space-y-4">
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">📊 Insight</h5>
                  <p className="text-gray-700">{suggestion.insight}</p>
                </div>
                
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">💡 Recommendation</h5>
                  <p className="text-gray-700">{suggestion.recommendation}</p>
                </div>
                
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">📚 Research Evidence</h5>
                  <p className="text-gray-700 italic">{suggestion.evidence}</p>
                </div>
                
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">🎯 Implementation Steps</h5>
                  <ul className="list-disc list-inside space-y-1 text-gray-700">
                    {suggestion.implementation.map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default SuggestionsList; 